import {
  CampaignStatusType,
  fallbackText,
  HelpTypes,
  HelpUserTypes,
  IAdminLead,
  LeadStatusType,
  PermissionsOfAdmin,
} from '@pharmaplan/common';

import { goToFallback } from '@pharmaplan/common/helpers/utils';
import { DynamicTableCell } from '../../../helpers/Constants';
import { getDayMonthDateYear } from '../../Reports/helpers';
import editIcon from '../../../assets/svg/updateNoteIcon.svg';
import sendIcon from '../../../assets/svg/sendInvite.svg';
import addCredit from '../../../assets/svg/createEntitlementIcon.svg';
import resendIcon from '../../../assets/svg/resendInvite.svg';
import historyIcon from '../../../assets/svg/showPassword.svg';
import viewProfileIcon from '../../../assets/svg/viewProfile.svg';

import deleteIcon from '../../../assets/svg/delete.svg';
import strings from '../../../localization';
import { IUserProfileLinkSignature } from '../../../hooks/Admin/useUserProfileLink';
import { selectAllTypes } from './LeadsHeader';

const { text, chip, bar, customLink } = DynamicTableCell;

const statusStyleMap = {
  [LeadStatusType.Pending]: {
    color: 'pharmacist.requested',
  },
  [LeadStatusType.InviteSent]: {
    color: 'primary.main',
  },
  [LeadStatusType.Registered]: {
    color: 'primary.main',
  },
  [LeadStatusType.RegistrationApproved]: {
    color: 'primary.main',
  },
  [LeadStatusType.RegistrationRejected]: {
    color: 'admin.notAssigned',
  },
  [LeadStatusType.Deleted]: {
    color: 'common.error',
  },
};

const leadStatusLabelMap = () =>
  ({
    [LeadStatusType.Pending]: strings.pending,
    [LeadStatusType.InviteSent]: strings.inviteSent,
    [LeadStatusType.Registered]: strings.registered,
    [LeadStatusType.RegistrationApproved]: strings.accountCreated,
    [LeadStatusType.RegistrationRejected]: strings.rejected,
    [LeadStatusType.Deleted]: strings.deleted,
  });

const safeDate = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  return getDayMonthDateYear(date);
};

interface ILeadRowsParams {
  data: Array<IAdminLead>;
  can: (permissions: PermissionsOfAdmin) => boolean;
  handleEdit: (id: string) => () => void;
  handleDelete: (id: string) => () => void;
  handleViewBookings: (id: string) => () => void;
  sendInvite: (id: Array<string>) => () => void;
  handlePress: (leadId: string) => () => void;
  selectedItems: Array<string>;
  goToUserProfile: IUserProfileLinkSignature;
  handleAddCredits: (leadIds: Array<string>, credits: number) => () => void;
  handleDetails: (payload: {
    name: string;
    email: string;
    phone: string;
    statusLabel: string;
    notes: string;
    statusStyle: Record<string, any>;
  }) => () => void;
  selectAllType: Array<selectAllTypes>;
  userType: HelpTypes;
  handleCampaignDetails: (payload: { campaignId: string | null }) => () => void;
}

export const leadHeaders = () =>
  [
    {
      key: '0',
      label: strings.name,
      sortingKey: 'name',
    },
    {
      key: '1',
      label: strings.email,
      sortingKey: 'email',
    },
    {
      key: '2',
      label: strings.campaignName,
      sortingKey: 'campaignName',
    },
    {
      key: '3',
      label: strings.broadcastGroup,
    },
    {
      key: '4',
      label: strings.invitesSent,
      sortingKey: 'inviteSentCount',
    },
    {
      key: '5',
      label: strings.lastInviteSent,
      sortingKey: 'lastInviteSentAt',
    },
    {
      key: '6',
      label: strings.status,
      sortingKey: 'status',
    },
    {
      key: '7',
      label: strings.action,
    },
  ];

export const leadRows = ({
  can,
  data,
  userType,
  handleEdit,
  sendInvite,
  handlePress,
  handleDelete,
  selectedItems,
  handleDetails,
  goToUserProfile,
  handleAddCredits,
  handleViewBookings,
  handleCampaignDetails,
}: ILeadRowsParams) =>
  data?.map((item) => {
    const {
      leadId,
      name,
      email,
      campaignName,
      lastInviteSentAt = '',
      phone = '',
      campaignStatus,
      inviteSentCount,
      status,
      notes,
      pharmacyId,
      totalCredits = 0,
      professionalId,
      campaignId,
      broadcastGroupName,
    } = item ?? {};

    const canAddCredit = can(PermissionsOfAdmin.AdditionalCreditAllocation);

    const isPharmacy = userType === HelpTypes.pharmacy;
    const isExistingUser = (!!pharmacyId || !!professionalId)
      && !inviteSentCount
      && !lastInviteSentAt;

    const isActive = campaignStatus === CampaignStatusType.Active;
    const isAccountCreated = status === LeadStatusType.RegistrationApproved;
    const isRegistered = status === LeadStatusType.Registered;
    const isPending = status === LeadStatusType.Pending;
    const isInviteSent = status === LeadStatusType.InviteSent;
    const isDeletedRejected = status === LeadStatusType.Deleted
      || status === LeadStatusType.RegistrationRejected;

    const showInvite = (isPending || isInviteSent || isExistingUser) && isActive;

    let actionItems: Array<{
      key: string;
      icon: string;
      tooltip: string;
      onClick: () => void;
      disabled?: boolean;
    }> = [
      {
        key: '1',
        icon: editIcon,
        tooltip: strings.edit,
        onClick: handleEdit(leadId),
      },
      {
        key: '2',
        icon: deleteIcon,
        tooltip: strings.delete,
        onClick: handleDelete(leadId),
      },
    ];

    const inviteIcons = () => {
      if (isExistingUser && canAddCredit) {
        actionItems.unshift({
          key: '0',
          icon: addCredit,
          tooltip: isActive
            ? strings.inviteExistingUser
            : strings.creditCannotBeEditedInactive,
          disabled: !isActive,
          onClick: handleAddCredits([leadId], totalCredits),
        });
        return;
      }

      if (!isActive) return;

      if (isPending) {
        actionItems.unshift({
          key: '0',
          icon: sendIcon,
          tooltip: strings.sendInvite,
          onClick: sendInvite([leadId]),
        });

        return;
      }

      actionItems.unshift({
        key: '0',
        icon: resendIcon,
        tooltip: strings.resendInvite,
        onClick: sendInvite([leadId]),
      });
    };

    inviteIcons();

    if (isDeletedRejected) {
      actionItems = [
        {
          key: '2',
          icon: deleteIcon,
          tooltip: strings.delete,
          onClick: handleDelete(leadId),
        },
      ];
    }

    if (isAccountCreated && !isExistingUser) {
      actionItems = [
        {
          key: '4',
          icon: viewProfileIcon,
          tooltip: strings.formatString(
            strings.view,
            strings.profile,
          ) as string,
          onClick: goToUserProfile({
            personnelId: isPharmacy ? pharmacyId : professionalId,
            userType: HelpUserTypes[userType as keyof typeof HelpUserTypes],
          }),
        },
        {
          key: '3',
          icon: historyIcon,
          tooltip: strings.viewBookings,
          onClick: handleViewBookings(leadId),
        },
      ];
    }

    if (isRegistered) {
      actionItems = [];
    }

    const statusLabel = leadStatusLabelMap()[status];

    return {
      key: leadId,
      data: [
        {
          key: '0',
          value: {
            label: name,
            handlePress: handleDetails({
              name,
              email,
              phone,
              statusLabel,
              notes: goToFallback(notes, fallbackText),
              statusStyle: statusStyleMap[status],
            }),
          },
          type: customLink,
          maxWidth: 220,
        },
        {
          key: '1',
          value: email,
          type: text,
          maxWidth: 220,
        },
        {
          key: '2',
          value: {
            label: campaignName,
            handlePress: handleCampaignDetails({ campaignId }),
          },
          type: customLink,
        },
        {
          key: '3',
          value: broadcastGroupName ?? '',
          type: text,
        },
        {
          key: '4',
          value: inviteSentCount,
          type: text,
        },
        {
          key: '5',
          value: safeDate(lastInviteSentAt),
          type: text,
        },
        {
          key: '6',
          value: {
            label: statusLabel,
            style: statusStyleMap[status],
            tooltipText:
              isRegistered
                ? strings.pendingActivationList
                : '',
          },
          type: chip,
        },
        {
          key: '7',
          value: actionItems.length ? actionItems : fallbackText,
          type: actionItems.length ? bar : text,
        },
        showInvite && {
          key: '8',
          value: {
            checked: selectedItems.includes(leadId),
            handlePress: handlePress(leadId),
          },
          isCenterAligned: true,
          type: DynamicTableCell.checkbox,
        },
      ],
    };
  });
